import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import axios from 'axios';
import config from '../config.js';

import '../styles/global.css';
import '../styles/buttons.css';

import PageHeader from '../components/general/pageHeader.js';
import PageTable from '../components/general/pageTable.js';
import DeleteConfirmation from '../components/general/deleteConfirmation.js';
import ManageForm from '../components/forms/manageForm.js';
import Loading from '../components/general/loading.js';
import useAuthenticate from '../components/authentication/authenticate.js';

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const ManageVendors = () => {
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [editingVendor, setEditingVendor] = useState(null);
    const [formOverlayOpen, setFormOverlayOpen] = useState(false);
    const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);

    const { isLoading } = useAuthenticate();

    const queryClient = useQueryClient();

    const { data: properties } = useQuery({
        queryKey: ['v2-properties'],
        queryFn: fetchProperties,
    });

    const { data: vendors, isLoading: isTableLoading } = useQuery({
        queryKey: ['v2-vendors', selectedProperty?.value],
        queryFn: () => fetchVendors(),
        enabled: !!selectedProperty,
    });

    // Auto-select first property
    useEffect(() => {
        if (properties?.length > 0 && !selectedProperty) {
            setSelectedProperty(properties[0]);
        }
    }, [properties, selectedProperty]);

    const handleDelete = (vendor) => {
        setSelectedVendor(vendor);
        setDeleteOverlayOpen(true);
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${config.API_URL}/v2/vendor/${selectedVendor._id}`, {
                headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}
            });

            setDeleteOverlayOpen(false);
            setSelectedVendor(null);
            await queryClient.invalidateQueries(['v2-vendors', selectedProperty?.value]);
            toast.success('Vendor deleted successfully!');
        } catch (error) {
            console.error('Error deleting vendor:', error);
            toast.error('Failed to delete vendor.');
        }
    };

    const handleSubmit = async (formData) => {
        try {
            const vendorData = {
                vendor: formData.name,
                _id: formData.id,
                property: selectedProperty?.value
            };

            if (editingVendor) {
                await axios.put(`${config.API_URL}/v2/vendor/${vendorData._id}`, vendorData, {
                    headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}
                });
                toast.success('Vendor updated successfully!');
            } else {
                await axios.post(`${config.API_URL}/v2/vendor`, vendorData, {
                    headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}
                });
                toast.success('Vendor created successfully!');
            }
            setFormOverlayOpen(false);
            setEditingVendor(null);

            // Refresh the vendor list after submission
            await queryClient.invalidateQueries(['v2-vendors', selectedProperty?.value]);
        } catch (error) {
            if (editingVendor) {
                toast.error('Failed to update vendor.');
            } else {
                toast.error('Failed to create vendor.');
            }
            console.error('Error submitting vendor:', error);
        }
    };

    return (
        <div className="w-full">
            <PageHeader pageName="Manage Vendors" />
            {isLoading && !selectedProperty ? (
                <Loading />
            ) : (
                <>
                    <div className="page-header">
                        <Autocomplete
                            options={properties}
                            value={selectedProperty}
                            onChange={(e, newVal) => {
                                if (newVal && newVal.value && newVal.label) {
                                    setSelectedProperty(newVal);
                                } else {
                                    setSelectedProperty(null);
                                }
                            }}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) =>
                                typeof option === 'string'
                                    ? option
                                    : option?.label || option?.name || ''
                            }
                            renderInput={params => <TextField {...params} label="Select Property" variant="outlined" fullWidth />}
                            sx={{ width: "350px" }}
                        />
                        <button onClick={() => setFormOverlayOpen(true)} className="header-button">
                            <i className="fas fa-add"></i>Vendor
                        </button>
                    </div>
                    {isTableLoading ? (
                        <Loading />
                    ) : (
                        <PageTable
                            rows={vendors?.filter(ven => ven.property?._id === selectedProperty?.value)}
                            columns={[
                                { label: 'Vendors', accessor: 'vendor' },
                            ]}
                            hasButtons={true}
                            hasEdit={true}
                            hasDelete={true}
                            onEdit={(vendor) => {
                                setEditingVendor(vendor);
                                setFormOverlayOpen(true);
                            }}
                            onDelete={handleDelete}
                        />
                    )}
                </>
            )}
            {formOverlayOpen && (
                <ManageForm
                    type="Vendor"
                    onSubmit={handleSubmit}
                    editingItem={editingVendor}
                    onClose={() => {
                        setFormOverlayOpen(false);
                        setEditingVendor(null);
                    }}
                />
            )}
            {deleteOverlayOpen && (
                <DeleteConfirmation
                    setIsOverlayOpen={setDeleteOverlayOpen}
                    onConfirm={confirmDelete}
                />
            )}
        </div>
    );
}

const fetchProperties = async () => {
    try {
        const response = await axios.get(`${config.API_URL}/v2/property`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
        return response.data.map(item => ({ value: item._id, label: item.name }));
    } catch (error) {
        console.error("Error fetching properties:", error);
        return [];
    }
};

const fetchVendors = async () => {
    try {
        const response = await axios.get(`${config.API_URL}/v2/vendor`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
        return response.data;
    } catch (error) {
        console.error("Error fetching vendors:", error);
        return [];
    }
};

export default ManageVendors;